import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { dataService } from '../../services/dataService'
import { ResourceAllocation, AllocationInterval } from '../../types'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Plus, EditIcon, Trash2 } from '../icons/Icons'
import { toDateOnly } from '../../utils/validators'
import s from './shared.module.css'

export default function Allocations() {
  const { organization } = useAuth()
  const { allocations, allAllocations, setAllocations, resetAllocations, projects, resources, resourceRoleTypes, refreshData } = useAppData()
  const orgId = organization!.id

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<ResourceAllocation | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [filters, setFilters] = useState({ projectId: '', resourceId: '', roleTypeId: '' })
  const [form, setForm] = useState({
    project_id: '', resource_id: '', role_type_id: '', allocation_start: '', allocation_end: '',
    daily_hours: 8, allocation_interval: 'Daily' as any, allocation_type: 'Forecast' as any,
    allocation_source: 'Manual' as any, allocation_entry_date: new Date().toISOString().substring(0, 10),
    billable: true, cost_rate_at_allocation: 0, billing_rate_at_allocation: 0, currency_at_allocation: 'USD' as any
  })

  const fetchRates = async (projectId: string, roleTypeId: string) => {
    if (!projectId || !roleTypeId) return
    try {
      const rates = await dataService.getEffectiveRates(orgId, projectId, roleTypeId)
      setForm(f => ({ ...f, cost_rate_at_allocation: rates.costRate, billing_rate_at_allocation: rates.billingRate, currency_at_allocation: rates.currency }))
    } catch {}
  }

  const openAdd = () => {
    setEditing(null)
    setForm({ project_id: '', resource_id: '', role_type_id: '', allocation_start: '', allocation_end: '', daily_hours: 8, allocation_interval: 'Daily', allocation_type: 'Forecast', allocation_source: 'Manual', allocation_entry_date: new Date().toISOString().substring(0, 10), billable: true, cost_rate_at_allocation: 0, billing_rate_at_allocation: 0, currency_at_allocation: 'USD' })
    setSaveError(''); setShowModal(true)
  }
  const openEdit = (a: ResourceAllocation) => {
    setEditing(a)
    setForm({ project_id: a.project_id, resource_id: a.resource_id, role_type_id: a.role_type_id, allocation_start: toDateOnly(a.allocation_start), allocation_end: toDateOnly(a.allocation_end), daily_hours: a.daily_hours, allocation_interval: a.allocation_interval, allocation_type: a.allocation_type, allocation_source: a.allocation_source, allocation_entry_date: toDateOnly(a.allocation_entry_date), billable: a.billable, cost_rate_at_allocation: a.cost_rate_at_allocation, billing_rate_at_allocation: a.billing_rate_at_allocation, currency_at_allocation: a.currency_at_allocation })
    setSaveError(''); setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (saving) return; setSaving(true); setSaveError('')
    try {
      if (editing) await dataService.updateAllocation(editing.id, form)
      else await dataService.createAllocation(orgId, form as any)
      setShowModal(false); await refreshData()
    } catch (err: any) { setSaveError(err?.message || 'Failed to save. Please try again.') } finally { setSaving(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this allocation?')) return
    await dataService.deleteAllocation(id); await refreshData()
  }

  const applyFilters = () => {
    let result = allAllocations
    if (filters.projectId) result = result.filter(a => a.project_id === filters.projectId)
    if (filters.resourceId) result = result.filter(a => a.resource_id === filters.resourceId)
    if (filters.roleTypeId) result = result.filter(a => a.role_type_id === filters.roleTypeId)
    setAllocations(result)
  }

  const getProjectName = (id: string) => projects.find(p => p.id === id)?.title || '—'
  const getResourceName = (id: string) => resources.find(r => r.id === id)?.title || '—'
  const getRoleName = (id: string) => resourceRoleTypes.find(r => r.id === id)?.title || '—'

  return (
    <div>
      <div className={s.pageHeader}>
        <div><h2 className={s.pageTitle}>Resource Allocations</h2><p className={s.pageSubtitle}>{allAllocations.length} allocation{allAllocations.length !== 1 ? 's' : ''}</p></div>
        <button className={s.primaryBtn} onClick={openAdd}><Plus size={14} /> Add Allocation</button>
      </div>

      <div className={s.card}>
        <div className={s.filterBar}>
          <select className={s.filterSelect} value={filters.projectId} onChange={e => setFilters(f => ({ ...f, projectId: e.target.value }))}>
            <option value="">All Projects</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select className={s.filterSelect} value={filters.resourceId} onChange={e => setFilters(f => ({ ...f, resourceId: e.target.value }))}>
            <option value="">All Resources</option>{resources.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
          <select className={s.filterSelect} value={filters.roleTypeId} onChange={e => setFilters(f => ({ ...f, roleTypeId: e.target.value }))}>
            <option value="">All Roles</option>{resourceRoleTypes.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
          <button className={s.primaryBtn} onClick={applyFilters}>Filter</button>
          <button className={s.secondaryBtn} onClick={() => { setFilters({ projectId: '', resourceId: '', roleTypeId: '' }); resetAllocations() }}>Reset</button>
        </div>

        <table className={s.table}>
          <thead><tr><th>Project</th><th>Resource</th><th>Role</th><th>Start</th><th>End</th><th>Hours</th><th>Interval</th><th>Type</th><th>Billable</th><th>Currency</th><th>Actions</th></tr></thead>
          <tbody>
            {allocations.length === 0 && <tr><td colSpan={11} className={s.emptyState}>No allocations found</td></tr>}
            {allocations.map(a => (
              <tr key={a.id}>
                <td style={{ fontWeight: 600 }}>{getProjectName(a.project_id)}</td>
                <td>{getResourceName(a.resource_id)}</td>
                <td>{getRoleName(a.role_type_id)}</td>
                <td style={{ fontSize: 12 }}>{a.allocation_start}</td>
                <td style={{ fontSize: 12 }}>{a.allocation_end}</td>
                <td style={{ fontSize: 12 }}>{a.daily_hours}{a.allocation_interval === 'Weekly' ? '/week' : a.allocation_interval === 'Monthly' ? '/month' : '/day'}</td>
                <td><Badge variant="info">{a.allocation_interval}</Badge></td>
                <td><Badge variant={a.allocation_type === 'Forecast' ? 'info' : 'success'}>{a.allocation_type}</Badge></td>
                <td><Badge variant={a.billable ? 'success' : 'neutral'}>{a.billable ? 'Yes' : 'No'}</Badge></td>
                <td>{a.currency_at_allocation}</td>
                <td><div className={s.actions}>
                  <button className={`${s.actionBtn} ${s.edit}`} onClick={() => openEdit(a)}><EditIcon size={14} /></button>
                  <button className={`${s.actionBtn} ${s.del}`} onClick={() => handleDelete(a.id)}><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showModal} title={editing ? 'Edit Allocation' : 'Add Allocation'} onClose={() => setShowModal(false)} maxWidth={600}
        footer={<><button className={s.secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button><button className={s.primaryBtn} form="allocForm" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></>}>
        <form id="allocForm" onSubmit={handleSubmit}>
          {saveError && <div className={s.formError}>{saveError}</div>}
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Project *</label>
              <select value={form.project_id} onChange={e => { setForm(f => ({ ...f, project_id: e.target.value })); fetchRates(e.target.value, form.role_type_id) }} required>
                <option value="">Select project...</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div className={s.formGroup}><label>Resource *</label>
              <select value={form.resource_id} onChange={e => setForm(f => ({ ...f, resource_id: e.target.value }))} required>
                <option value="">Select resource...</option>{resources.filter(r => r.active).map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
              </select>
            </div>
          </div>
          <div className={s.formGroup}><label>Role Type *</label>
            <select value={form.role_type_id} onChange={e => { setForm(f => ({ ...f, role_type_id: e.target.value })); fetchRates(form.project_id, e.target.value) }} required>
              <option value="">Select role...</option>{resourceRoleTypes.filter(r => r.active).map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Start Date *</label><input type="date" value={form.allocation_start} onChange={e => setForm(f => ({ ...f, allocation_start: e.target.value }))} required /></div>
            <div className={s.formGroup}><label>End Date *</label><input type="date" value={form.allocation_end} onChange={e => setForm(f => ({ ...f, allocation_end: e.target.value }))} required /></div>
          </div>
          <div className={s.formGroup}>
            <label>Allocation Interval *</label>
            <select value={form.allocation_interval} onChange={e => setForm(f => ({ ...f, allocation_interval: e.target.value as AllocationInterval }))} required>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
            <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
              {form.allocation_interval === 'Daily' && 'Hours will be allocated per working day'}
              {form.allocation_interval === 'Weekly' && 'Hours will be distributed evenly across working days in each week'}
              {form.allocation_interval === 'Monthly' && 'Hours will be distributed evenly across working days in each month'}
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>
                {form.allocation_interval === 'Daily' && 'Hours per Day *'}
                {form.allocation_interval === 'Weekly' && 'Hours per Week *'}
                {form.allocation_interval === 'Monthly' && 'Hours per Month *'}
              </label>
              <input
                type="number"
                min="0.5"
                max={form.allocation_interval === 'Daily' ? 24 : form.allocation_interval === 'Weekly' ? 168 : 744}
                step="0.5"
                value={form.daily_hours}
                onChange={e => setForm(f => ({ ...f, daily_hours: parseFloat(e.target.value) || 8 }))}
                required
              />
            </div>
            <div className={s.formGroup}><label>Allocation Type *</label>
              <select value={form.allocation_type} onChange={e => setForm(f => ({ ...f, allocation_type: e.target.value as any }))}>
                <option value="Forecast">Forecast</option><option value="Actual">Actual</option>
              </select>
            </div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Cost Rate at Allocation</label><input type="number" step="0.01" value={form.cost_rate_at_allocation} onChange={e => setForm(f => ({ ...f, cost_rate_at_allocation: parseFloat(e.target.value) || 0 }))} /></div>
            <div className={s.formGroup}><label>Billing Rate at Allocation</label><input type="number" step="0.01" value={form.billing_rate_at_allocation} onChange={e => setForm(f => ({ ...f, billing_rate_at_allocation: parseFloat(e.target.value) || 0 }))} /></div>
          </div>
          <div className={s.formRow}>
            <div className={s.formGroup}><label>Currency at Allocation</label>
              <select value={form.currency_at_allocation} onChange={e => setForm(f => ({ ...f, currency_at_allocation: e.target.value as any }))}>
                <option value="USD">USD</option><option value="GBP">GBP</option><option value="INR">INR</option>
              </select>
            </div>
            <div className={s.checkboxGroup} style={{ alignSelf: 'flex-end', paddingBottom: 8 }}>
              <input type="checkbox" id="allocBillable" checked={form.billable} onChange={e => setForm(f => ({ ...f, billable: e.target.checked }))} />
              <label htmlFor="allocBillable">Billable</label>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}
